import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import MediaCarousel from "../components/MediaCarousel";
import SEO, { buildBreadcrumb } from "../components/SEO";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import TravelRiderCard from "../components/TravelRiderCard";
import TechRiderLink from "../components/TechRiderLink";
import { consumeArtistClickSource, track } from "../analytics/tracker";
import CitySearchField from "../components/CitySearchField";
import api, { fmtINRFull, mediaUrl, thumbUrl } from "../lib/api";
import { loadIndianCityCatalog } from "../lib/indianCities";
import { getArtistEventTypes } from "./artistProfileFacts";
import { useAuth } from "../lib/auth";
import LoginGate, { useLoginGate } from "../components/LoginGate";
import { useToast } from "../lib/toast";
import { ChevronLeft, ChevronRight, Clock3, Eye, Heart, MessageCircle } from "lucide-react";
import { resolveTravelTerms, travelCostDisclosure } from "../lib/travelTerms";

const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PROFILE_GALLERY_LIMIT = 8;
// One token per artist per SPA visit. This survives React Strict Mode's
// development-only effect replay while a browser refresh creates a new view.
const profileViewTokens = new Map();

function profileViewToken(artistId) {
  if (!profileViewTokens.has(artistId)) {
    const token = globalThis.crypto?.randomUUID?.()
      || String(Date.now()) + "-" + Math.random().toString(36).slice(2);
    profileViewTokens.set(artistId, token);
  }
  return profileViewTokens.get(artistId);
}

export default function ArtistProfile() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [about, setAbout] = useState(null); // Iter 55 — questionnaire answers
  const [reviews, setReviews] = useState([]);
  const [reviewEvents, setReviewEvents] = useState([]);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  // "" | "booking:<id>" (own booking) | "event:<id>" (artist's public event) | "other"
  const [reviewEventChoice, setReviewEventChoice] = useState("");
  const [reviewEventNote, setReviewEventNote] = useState("");
  const [reviewPhotos, setReviewPhotos] = useState([]); // [{ name, url }] data URLs
  const [reviewVideos, setReviewVideos] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewsError, setReviewsError] = useState("");
  const [reviewEventsError, setReviewEventsError] = useState("");
  const [artistEventGallery, setArtistEventGallery] = useState([]);
  const [tab, setTab] = useState("about");
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const reviewsScrollerRef = useRef(null);
  const [artistId, setArtistId] = useState(id);
  const [lightbox, setLightbox] = useState(null); // { items: MediaItem[], idx: number }
  // Iter 52.6 — Venue-first booking flow. The customer must type the event
  // city BEFORE the "Book Now" button unlocks. We hit /artists/:id/quote to
  // determine outstation status + adjusted quoted price so the profile shows
  // the correct number and (when outstation) surfaces mandatory terms.
  const [eventCity, setEventCity] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [popularCityOptions, setPopularCityOptions] = useState([]);
  const [cityAliases, setCityAliases] = useState({});
  const [cityOptionsError, setCityOptionsError] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [outstationAck, setOutstationAck] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  // Iter 70 — Show a friendly guardrail modal when a guest tries to book,
  // instead of instantly redirecting them to /login and losing context.
  const [showLoginGate, setShowLoginGate] = React.useState(false);
  const [loginGateReturnTo, setLoginGateReturnTo] = React.useState(null);
  // Iter 71 — Extended LoginGate coverage to Favorite / Message Artist /
  // Availability calendar picks. Uses the shared `useLoginGate` hook so
  // each guest action gets its own contextual title + message override.
  const secondaryGate = useLoginGate();
  // Favorites are stored locally per-browser as an MVP — swap for a real
  // /api/customer/favorites endpoint when a proper account-scoped list is
  // needed. localStorage keeps the code side-effect-free for logged-out
  // browsing sessions too (their picks travel with them if they sign up).
  const favoritesKey = "bt_favorites";
  const [isFavorite, setIsFavorite] = React.useState(false);
  // Iter 73 — Location popup: instead of a hard alert("enter city first"),
  // opening the Book Now with no city shows a friendly modal that captures
  // it, auto-fills the sidebar city field, then resumes the flow. Declared
  // up here (BEFORE the early returns below) so hook order stays stable.
  const [locationPrompt, setLocationPrompt] = React.useState({ open: false, value: "", date: "" });
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(favoritesKey);
      const list = raw ? JSON.parse(raw) : [];
      setIsFavorite(list.includes(artistId));
    } catch { /* ignore */ }
  }, [artistId]);

  useEffect(() => {
    loadIndianCityCatalog("artist")
      .then(({ popular, cities, aliases }) => {
        setPopularCityOptions(popular.map((city) => city.name));
        setCityOptions(cities.map((city) => city.name));
        setCityAliases(aliases);
      })
      .catch(() => setCityOptionsError(true));
  }, []);

  useEffect(() => {
    // Support both /artist/:uuid and SEO-friendly /artist/:slug URLs
    const load = async () => {
      let uid = id;
      if (!UUID_RX.test(id)) {
        try {
          const r = await api.get(`/artists/slug/${id}`);
          uid = r.data.user_id;
        } catch (_) { setData({ notFound: true }); return; }
      }
      setArtistId(uid);
      try {
        const r = await api.get(`/artists/${uid}`);
        setData(r.data);
        track("artist_profile_view", {
          artist_id: uid,
          category: r.data.profile?.category,
          city: r.data.profile?.city,
          source: consumeArtistClickSource(uid)
            || (document.referrer.includes("/planner") ? "planner" : document.referrer.includes("/search") ? "search" : document.referrer ? "external" : "direct"),
        });
        // Counting is intentionally a separate idempotent event. Profile
        // reads can be replayed by React Strict Mode or retried by a browser,
        // but one actual SPA visit must count only once.
        api.post(`/artists/${uid}/view`, { view_token: profileViewToken(uid) }).catch(() => {});
        // A package must be chosen explicitly before booking.
        const requestedPkg = searchParams.get("pkg");
        const requestedPackage = r.data.packages.find((p) => p.id === requestedPkg);
        if (requestedPackage) setSelectedPkg(requestedPackage);
        // Iter 55 — Pull the artist's questionnaire answers so the About
        // tab can render every declared detail (travel, hospitality,
        // technical rider, availability windows, category-specific
        // preferences…). This endpoint is public — same access rules as
        // the profile itself.
        api.get(`/artists/${uid}/about`).then((ar) => setAbout(ar.data)).catch(() => setAbout(null));
        // Iter 74 — Silently record the view for logged-in customers so
        // their dashboard can surface "Recently Viewed" artists across
        // any device. Failures are non-fatal (401 = guest browsing).
        if (user && (user.role === "customer" || user.role === "corporate")) {
          api.post(`/customer/recent-views/${uid}`).catch(() => {});
        }
      } catch (_) { setData({ notFound: true }); }
    };
    load();
  }, [id, searchParams, user]);

  useEffect(() => {
    if (!UUID_RX.test(artistId)) return;
    api.get(`/reviews/artist/${artistId}`)
      .then((response) => {
        setReviews(response.data);
        setReviewsError("");
      })
      .catch(() => setReviewsError("Reviews could not be loaded. Please refresh and try again."));
  }, [artistId]);

  useEffect(() => {
    if (!["customer", "corporate"].includes(user?.role)) {
      setMyReview(null);
      return;
    }
    api.get("/reviews/mine")
      .then((response) => {
        setMyReview((response.data || []).find((review) => review.artist_id === artistId) || null);
      })
      .catch(() => setMyReview(null));
  }, [artistId, user?.role]);

  useEffect(() => {
    if (!UUID_RX.test(artistId) || !["customer", "corporate"].includes(user?.role)) {
      setReviewEvents([]);
      setReviewEventsError("");
      return;
    }
    api.get(`/reviews/artist/${artistId}/events`)
      .then((response) => {
        setReviewEvents(response.data);
        setReviewEventsError("");
      })
      .catch(() => setReviewEventsError("Your past events could not be loaded. You can still submit a review without selecting an event."));
  }, [artistId, user]);

  useEffect(() => {
    if (!UUID_RX.test(artistId)) {
      setArtistEventGallery([]);
      return;
    }
    api.get(`/artists/${artistId}/events-gallery`)
      .then((response) => setArtistEventGallery(response.data?.events || []))
      .catch(() => setArtistEventGallery([]));
  }, [artistId]);

  // Debounced quote fetch when the customer types an event city. We wait
  // 400 ms after their last keystroke to avoid firing on every character
  // and only fire when at least 2 chars are present so single-letter typos
  // don't ping the backend.
  useEffect(() => {
    if (!artistId) return;
    const city = (eventCity || "").trim();
    if (city.length < 2) { setQuote(null); return; }
    setQuoting(true);
    const t = setTimeout(() => {
      api.get(`/artists/${artistId}/quote?city=${encodeURIComponent(city)}`)
        .then((r) => setQuote(r.data))
        .catch(() => setQuote(null))
        .finally(() => setQuoting(false));
    }, 400);
    return () => { clearTimeout(t); setQuoting(false); };
  }, [artistId, eventCity]);

  // If the customer switches package after we already quoted, keep the
  // quote — the outstation status doesn't depend on package choice.

  if (data?.notFound) {
    return (
      <div>
        <SEO title="Artist Not Found" noindex path={`/artist/${id}`} />
        <Nav />
        <section className="section container" style={{ minHeight: "50vh", textAlign: "center", paddingTop: "var(--space-16)" }}>
          <div style={{ fontSize: 72 }}></div>
          <h1>Artist Not Found</h1>
          <Link to="/search" className="btn btn-gold">Browse all artists →</Link>
        </section>
</div>
    );
  }
  if (!data) return (
    <div>
      <Nav />
      <div className="loading"><div className="spinner" /></div>
    </div>
  );

  const { profile, packages, media, availability } = data;
  const bioWords = (profile.bio || "").trim().split(/\s+/).filter(Boolean);
  const bioHasMore = bioWords.length > 50;
  const displayedBio = aboutExpanded || !bioHasMore
    ? profile.bio || "No bio yet."
    : `${bioWords.slice(0, 50).join(" ")}…`;
  const artistEventTypes = getArtistEventTypes(profile, about);
  const galleryMedia = media.filter((m) => m.type === "gallery");
  const videoMedia = media.filter((m) => m.type === "video" || m.type === "reel");
  const navigateProfileSection = (section) => {
    setTab(section);
    document.getElementById(`artist-section-${section}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitReview = async (event) => {
    event.preventDefault();
    if (!user) return;
    if (!reviewRating) {
      toast("Choose a star rating first", "error");
      return;
    }
    if (!reviewText.trim()) {
      toast("Write a review before submitting", "error");
      return;
    }
    if (!editingReviewId && reviewEventChoice === "other" && !reviewEventNote.trim()) {
      toast("Describe the event, or choose “No specific event”", "error");
      return;
    }
    setReviewSubmitting(true);
    try {
      if (editingReviewId) {
        await api.put(`/reviews/${editingReviewId}`, {
          rating: reviewRating,
          text: reviewText.trim(),
        });
        setMyReview((review) => review ? {
          ...review,
          rating: reviewRating,
          text: reviewText.trim(),
          moderated: "pending",
          reply: null,
        } : review);
        setReviews((current) => current.filter((review) => review.id !== editingReviewId));
        setEditingReviewId(null);
        setReviewRating(0);
        setReviewText("");
        toast("Review updated — awaiting admin approval.");
        return;
      }
      const payload = {
        artist_id: profile.user_id,
        rating: reviewRating,
        text: reviewText.trim(),
      };
      if (reviewEventChoice.startsWith("booking:")) payload.booking_id = reviewEventChoice.slice(8);
      else if (reviewEventChoice.startsWith("event:")) payload.event_id = reviewEventChoice.slice(6);
      else if (reviewEventChoice === "other") payload.event_note = reviewEventNote.trim();
      if (reviewPhotos.length) payload.photos = reviewPhotos.map((file) => file.url);
      if (reviewVideos.length) payload.videos = reviewVideos.map((file) => file.url);
      await api.post("/reviews", payload);
      setReviewRating(0);
      setReviewText("");
      setReviewEventChoice("");
      setReviewEventNote("");
      setReviewPhotos([]);
      setReviewVideos([]);
      api.get("/reviews/mine")
        .then((response) => {
          setMyReview((response.data || []).find((review) => review.artist_id === artistId) || null);
        })
        .catch(() => {});
      toast("Review submitted for verification. It will appear here after approval.");
    } catch (error) {
      toast(error?.response?.data?.detail || "Could not submit your review", "error");
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Per-file limits mirror the API (5 photos ≤ 5 MB, 2 videos ≤ 30 MB). Files are
  // sent base64-encoded in one request, so the total is also capped to stay under
  // a 20 MB proxy body limit (deploy/nginx-booktalent.conf).
  const REVIEW_MEDIA_TOTAL_BYTES = 14 * 1024 * 1024;
  const REVIEW_MEDIA_LIMITS = {
    photo: { max: 5, bytes: 5 * 1024 * 1024, label: "5 MB", prefix: "image/" },
    video: { max: 2, bytes: 30 * 1024 * 1024, label: "30 MB", prefix: "video/" },
  };
  const addReviewMedia = async (fileList, kind) => {
    const limits = REVIEW_MEDIA_LIMITS[kind];
    const current = kind === "photo" ? reviewPhotos : reviewVideos;
    const setter = kind === "photo" ? setReviewPhotos : setReviewVideos;
    const files = Array.from(fileList || []);
    const accepted = [];
    let totalBytes = [...reviewPhotos, ...reviewVideos].reduce((sum, item) => sum + (item.size || 0), 0);
    for (const file of files) {
      if (!file.type.startsWith(limits.prefix)) { toast(`${file.name} isn't a ${kind}`, "error"); continue; }
      if (file.size > limits.bytes) { toast(`${file.name} is larger than ${limits.label}`, "error"); continue; }
      if (current.length + accepted.length >= limits.max) { toast(`You can add up to ${limits.max} ${kind}s`, "error"); break; }
      if (totalBytes + file.size > REVIEW_MEDIA_TOTAL_BYTES) { toast("Photos and videos together can be up to 14 MB per review", "error"); continue; }
      totalBytes += file.size;
      // eslint-disable-next-line no-await-in-loop
      const url = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      accepted.push({ name: file.name, size: file.size, url });
    }
    if (accepted.length) setter((items) => [...items, ...accepted]);
  };

  const startEditingReview = () => {
    if (!myReview) return;
    setEditingReviewId(myReview.id);
    setReviewRating(Number(myReview.rating) || 0);
    setReviewText(myReview.text || "");
    setReviewEventChoice("");
    document.getElementById("artist-review-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const cancelEditingReview = () => {
    setEditingReviewId(null);
    setReviewRating(0);
    setReviewText("");
  };

  const techRiderFile = quote?.tech_rider_file || data?.tech_rider_file;
  const isOutstation = !!quote?.is_outstation;
  const cityTouched = (eventCity || "").trim().length >= 2;
  const quotedPackage = quote?.packages?.find((pkg) => pkg.id === selectedPkg?.id) || selectedPkg || {};
  const travelTerms = resolveTravelTerms(quotedPackage, quote?.rider || {});
  const requiresTravelTermsAck = isOutstation && travelTerms.requiresAcknowledgement;
  const bookingBlocked =
    !cityTouched ||
    quoting ||
    !quote ||
    quote?.booking_allowed === false ||
    (requiresTravelTermsAck && !outstationAck);

  // Iter 73 — Location popup: instead of a hard alert("enter city first"),
  // opening the Book Now with no city shows a friendly modal that captures
  // it, auto-fills the sidebar city field, then resumes the flow.
  const startBooking = (overrideCity) => {
    const cityValue = (overrideCity ?? eventCity ?? "").trim();
    // Guard #1: venue must be entered — but instead of failing hard, open
    // the Location popup so the customer can supply it in-place.
    if (cityValue.length < 2) {
      setLocationPrompt({ open: true, value: eventCity || "", date: "" });
      return;
    }
    if (!quote && !overrideCity) {
      // Only require the pre-fetched quote when the city was already in
      // state (typed by the user). When we're resuming from the popup we
      // pass the new city → let the flow continue and re-quote inside
      // BookingFlow.
      alert("Fetching quote — please wait a moment.");
      return;
    }
    // Guard #2: outstation acknowledgement must be ticked for cross-city.
    if (quote?.booking_allowed === false) {
      alert(quote.booking_block_reason || "This artist is not available for the selected event location.");
      return;
    }
    if (requiresTravelTermsAck && !outstationAck && !overrideCity) {
      alert("Please acknowledge the travel costs payable directly to the artist.");
      return;
    }
    // Guard #3: role checks.
    if (user?.role === "artist") { alert("Artists cannot book themselves"); return; }
    if (user?.role === "agency") { alert("Please log in as a Customer to book an artist."); return; }

    // Thread city + outstation-ack + pkg through to the booking flow so the
    // customer never has to re-enter them post-login.
    const qs = new URLSearchParams();
    if (selectedPkg?.id) qs.set("pkg", selectedPkg.id);
    qs.set("city", cityValue);
    if (requiresTravelTermsAck && outstationAck && !overrideCity) qs.set("travel_terms_acknowledged", "1");
    const back = `/book/${artistId}?${qs.toString()}`;

    if (!user) {
      // Iter 70 — Show contextual login modal instead of jumping straight
      // to /login. Save intent to session storage so we can resume on the
      // same booking flow post-auth.
      try { sessionStorage.setItem("bt_post_login_redirect", back); } catch { /* ignore */ }
      setLoginGateReturnTo(back);
      setShowLoginGate(true);
      return;
    }
    nav(back);
  };

  // Iter 73 — Submit handler for the Location popup.
  const submitLocationPrompt = (e) => {
    e?.preventDefault();
    const city = (locationPrompt.value || "").trim();
    if (city.length < 2) return;
    // Update the sidebar city input so the customer never has to re-type
    // the same location again. useEffect on eventCity re-quotes.
    setEventCity(city);
    const selectedDate = locationPrompt.date;
    setLocationPrompt({ open: false, value: "", date: "" });
    if (selectedDate) {
      const qs = new URLSearchParams();
      if (selectedPkg?.id) qs.set("pkg", selectedPkg.id);
      qs.set("city", city);
      qs.set("date", selectedDate);
      nav(`/book/${artistId}?${qs.toString()}`);
      return;
    }
    // Continue Book Now with the freshly-entered city so guests hit the
    // LoginGate → login → back to /book/{id}?city=... flow immediately.
    // We pass `city` as override so we don't wait for the quote effect.
    setTimeout(() => startBooking(city), 30);
  };

  // Iter 71 — Guest-gated secondary actions.
  const toggleFavorite = secondaryGate.requireAuth(() => {
    try {
      const raw = localStorage.getItem(favoritesKey);
      const list = raw ? JSON.parse(raw) : [];
      const next = list.includes(artistId)
        ? list.filter((id) => id !== artistId)
        : [...list, artistId];
      localStorage.setItem(favoritesKey, JSON.stringify(next));
      setIsFavorite(next.includes(artistId));
      toast(next.includes(artistId) ? "Saved to favorites" : "Removed from favorites");
    } catch { /* ignore */ }
  }, {
    title: "Save this artist to your favorites",
    message: "Create a free BookTalent account to save artists you love and revisit them anytime.",
  });

  const messageArtist = secondaryGate.requireAuth(() => {
    // Chat unlocks only after a paid booking (business rule). Nudge the
    // customer to start the booking flow if they want to talk to the
    // artist directly.
    toast("Chat unlocks after you book & pay. Please Book Now to continue.");
  }, {
    title: "Sign in to contact this artist",
    message: "You'll need a free BookTalent account to message artists and confirm your booking.",
  });

  const handleAvailabilityPick = (dateStr) => {
    if (user?.role === "artist" || user?.role === "agency") return;
    if (!eventCity.trim()) {
      setLocationPrompt({ open: true, value: "", date: dateStr });
      return;
    }
    if (!user) {
      secondaryGate.promptLogin({
        title: "Sign in to check availability",
        message: "Create a free BookTalent account to reserve a date and start your booking.",
      });
      return;
    }
    // Customers with a city selected can continue directly with the date
    // pre-filled so they don't lose the calendar selection.
    const qs = new URLSearchParams();
    if (selectedPkg?.id) qs.set("pkg", selectedPkg.id);
    if (eventCity.trim()) qs.set("city", eventCity.trim());
    if (dateStr) qs.set("date", dateStr);
    nav(`/book/${artistId}?${qs.toString()}`);
  };

  // ── SEO JSON-LD (Person + Offer for the cheapest package) ───────────
  const cheapestPkg = (packages || []).slice().sort((a, b) => (a.price || 0) - (b.price || 0))[0];
  const selectPackage = (pkg) => {
    setSelectedPkg(pkg);
    setOutstationAck(false);
  };
  const scrollReviews = (direction) => {
    const scroller = reviewsScrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * scroller.clientWidth * 0.85,
      behavior: "smooth",
    });
  };
  const seoImg = profile.profile_image ? mediaUrl(profile.profile_image) : undefined;
  const seoPath = `/artist/${profile.slug || artistId}`;
  const personLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: profile.stage_name,
    jobTitle: profile.category,
    address: { "@type": "PostalAddress", addressLocality: profile.city, addressCountry: "IN" },
    image: seoImg,
    url: `https://booktalent.in${seoPath}`,
    ...(cheapestPkg ? { makesOffer: { "@type": "Offer", price: cheapestPkg.price, priceCurrency: "INR", name: cheapestPkg.name } } : {}),
  };

  return (
    <div data-testid="artist-profile-page">
      <SEO
        title={`Book ${profile.stage_name} — ${profile.category} in ${profile.city}`}
        description={`Book ${profile.stage_name}, a verified ${profile.category} from ${profile.city}. Starting ₹${profile.starting_price || cheapestPkg?.price || ""}. Check availability and packages on BookTalent.`}
        keywords={`book ${profile.stage_name}, ${profile.category} ${profile.city}, hire ${profile.category}, ${profile.category} for wedding`}
        image={seoImg}
        path={seoPath}
        jsonLd={[
          personLd,
          buildBreadcrumb([
            { name: "Home", url: "/" },
            { name: "Artists", url: "/search" },
            { name: profile.category, url: `/search?category=${encodeURIComponent(profile.category)}` },
            { name: profile.stage_name, url: seoPath },
          ]),
        ]}
      />
      <Nav />
      <div className="container" style={{ paddingTop: "var(--space-4)", paddingBottom: "var(--space-8)" }}>
        <div className="text-muted fs-12 mb-16">
          <Link to="/">Home</Link> › <Link to="/search">Artists</Link> › <Link to={`/search?category=${encodeURIComponent(profile.category)}`}>{profile.category}</Link> ›{" "}
          <span style={{ color: "var(--white)" }}>{profile.stage_name}</span>
        </div>

        <div className="artist-profile-page" data-testid="artist-profile-layout">
        <div style={{
          aspectRatio: "10 / 3", borderRadius: 18,
          background: profile.cover_image ? "#14121a" : "linear-gradient(135deg, #1A0B3B, #6D28D9)",
          display: "grid", placeItems: "center",
          fontSize: profile.cover_image ? 0 : 100,
          position: "relative", overflow: "hidden",
        }} data-testid="profile-cover-banner">
          {profile.cover_image && (() => {
            const coverSrc = `${mediaUrl(profile.cover_image)}?v=${profile.updated_at || ""}`;
            return (
              <>
                {/* Blurred fill behind the banner, so any shape of upload is shown whole without empty bars. */}
                <div className="profile-cover-fill" aria-hidden="true" style={{ backgroundImage: `url(${coverSrc})` }} />
                <img className="profile-cover-image" src={coverSrc} alt={`${profile.stage_name} banner`} data-testid="profile-cover-image" />
              </>
            );
          })()}
          {/* Iter 76.5 — Featured Reel: the artist's hero video plays
              muted + looped over the cover image. Falls back gracefully
              when no reel is set. */}
          {profile.featured_video_id && (
            <video
              data-testid="featured-reel-video"
              src={mediaUrl(profile.featured_video_id)}
              poster={`${api.defaults.baseURL}/media/${profile.featured_video_id}/thumb`}
              autoPlay muted loop playsInline preload="metadata"
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                objectFit: "cover", zIndex: 0,
              }}
            />
          )}
          {!profile.cover_image && !profile.featured_video_id && (profile.emoji || "")}
          <div style={{ position: "absolute", inset: 0, background: profile.featured_video_id ? "linear-gradient(180deg, rgba(9,9,18,0.15) 40%, rgba(9,9,18,0.75))" : (profile.cover_image ? "transparent" : "linear-gradient(180deg, transparent 40%, rgba(9,9,18,0.8))"), zIndex: 1 }} />
        </div>

        <div className="profile-header-row" data-testid="profile-header">
          <div
            className="avatar avatar-xl profile-header-avatar"
            style={{
              background: profile.profile_image
                ? `url(${mediaUrl(profile.profile_image)}?v=${profile.updated_at || ""}) center/cover`
                : "linear-gradient(135deg, var(--purple), var(--gold))",
              fontSize: profile.profile_image ? 0 : undefined,
            }}
            data-testid="profile-avatar"
          >
            {!profile.profile_image && (profile.emoji || "")}
          </div>
          <div className="profile-header-info">
            <h1 className="font-serif profile-name" data-testid="artist-name">{profile.stage_name}</h1>
            <div className="artist-profile-meta">
              {(profile.categories?.length ? profile.categories : [profile.category]).filter(Boolean).map((category, index) => (
                <span className="artist-profile-category" key={category} data-testid={index === 0 ? "artist-profession" : `artist-profession-${index + 1}`}>{category}</span>
              ))}
              <span className="artist-profile-meta-separator" aria-hidden="true">·</span>
              <span className="artist-profile-city">{profile.city}</span>
              {profile.kyc_status === "approved" && <span className="pill pill-green">✓ KYC Verified</span>}
            </div>
          </div>
          <div className="profile-header-cta">
            <div className="artist-profile-response">
              <Clock3 size={14} aria-hidden="true" />
              <span>Responds in <strong>~2 hrs</strong></span>
            </div>
            {Number(profile.profile_views) > 99 && (
              <div className="artist-profile-views">
                <Eye size={14} aria-hidden="true" />
                <span><strong>{Number(profile.profile_views).toLocaleString("en-IN")}</strong> profile views</span>
              </div>
            )}
          </div>
        </div>

        <div className="artist-profile-stats" data-testid="artist-trust-belt">
          {Number(profile.answers?.events_performed) > 0 && (
              <div className="artist-profile-stat" data-testid="stat-events">
                <span className="artist-profile-stat-value">{Number(profile.answers.events_performed).toLocaleString("en-IN")}</span>
                <span>Events performed</span>
              </div>
          )}
          <div className="artist-profile-stat" data-testid="stat-experience">
            <span className="artist-profile-stat-value">{profile.experience_years || 8}</span>
            <span>Years on stage</span>
          </div>
        </div>

            <nav className="tab-bar artist-profile-tabs" data-testid="profile-tabs" aria-label="Artist profile sections">
              {["about", "packages", "media", "availability", "reviews"].map((t) => (
                <button key={t} type="button" className={`tab-btn ${tab === t ? "active" : ""}`} onClick={() => navigateProfileSection(t)} data-testid={`tab-${t}`}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </nav>

          <div className="profile-main-grid artist-profile-content-grid">
            <div className="artist-profile-main-column">
            <section id="artist-section-about" className="card card-pad artist-profile-section" data-testid="tab-about-content">
                <h2 className="artist-profile-section-title">About {profile.stage_name.split(" ")[0]}</h2>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "var(--white-muted)", marginBottom: "var(--space-6)" }}>
                  {displayedBio}
                </p>
                {bioHasMore && (
                  <button
                    type="button"
                    className="artist-bio-toggle"
                    aria-expanded={aboutExpanded}
                    onClick={() => setAboutExpanded((expanded) => !expanded)}
                    data-testid="artist-bio-toggle"
                  >
                    {aboutExpanded ? "View less" : "View more"}
                  </button>
                )}
                <div className="artist-profile-facts">
                  <div>
                    <div className="text-muted fs-11 mb-4">LANGUAGES</div>
                    <div className="fs-13">{(profile.languages || []).join(" · ") || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted fs-11 mb-4">GENRES</div>
                    <div className="fs-13">{(profile.genres || []).join(" · ") || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted fs-11 mb-4">EVENT TYPES</div>
                    <div className="fs-13">{artistEventTypes.join(" · ") || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted fs-11 mb-4">TRAVEL</div>
                    <div className="fs-13">{profile.travel_range || "Pan India"}</div>
                  </div>
                </div>
                {/* Iter 55 — Questionnaire-driven details, grouped by section. */}
                <QuestionnairePanel about={about} onMediaClick={() => navigateProfileSection("media")} />
            </section>

            <section id="artist-section-packages" className="card card-pad artist-profile-section" data-testid="tab-packages-content">
                <h2 className="artist-profile-section-title">Pricing Packages</h2>
                {packages.length === 0 ? (
                  <div className="empty"><div className="empty-title">No packages available yet</div></div>
                ) : (
                  <div className="grid grid-3">
                    {packages.map((p) => (
                      <div
                        key={p.id}
                        className={`pkg-card ${p.is_popular ? "popular" : ""} ${selectedPkg?.id === p.id ? "selected" : ""}`}
                        data-testid={`package-card-${p.id}`}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedPkg?.id === p.id}
                        onClick={() => selectPackage(p)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            selectPackage(p);
                          }
                        }}
                      >
                        {p.is_popular && <span className="popular-tag">MOST POPULAR</span>}
                        {selectedPkg?.id === p.id && <span className="package-selected-tag">SELECTED</span>}
                        <div className="pkg-name">{p.name}</div>
                        <div className="text-muted fs-12 mb-12"> {p.duration}</div>
                        <div className="pkg-price">{fmtINRFull(p.price)} <span style={{ fontSize: 12, color: "var(--white-muted)", fontWeight: 400 }}>/event</span></div>
                        <ul className="pkg-features">{p.features.map((f, i) => <li key={`${p.id}-f-${i}-${f}`}>{f}</li>)}</ul>
                      </div>
                    ))}
                  </div>
                )}
            </section>

            <section id="artist-section-media" className="card card-pad artist-profile-section" data-testid="tab-media-content">
                <h2 className="artist-profile-section-title">Performance Gallery</h2>
                {media.filter((item) => item.type !== "kyc").length === 0 ? (
                  <div className="empty"><div className="empty-icon"></div><div className="empty-title">No media yet</div></div>
                ) : (() => {
                  const viewable = media.filter((m) => m.type !== "kyc");
                  const items = viewable.map((m) => ({
                    id: m.id,
                    src: mediaUrl(m.id),
                    isVideo: m.mime?.startsWith("video/"),
                    title: m.title || "",
                  }));
                  const profileItems = items.slice(0, PROFILE_GALLERY_LIMIT);
                  return (
                    <>
                      <div className="media-grid" data-testid="artist-profile-media-grid">
                        {profileItems.map((it, i) => (
                          <button
                            key={it.id}
                            type="button"
                            className="media-tile"
                            data-testid={`media-${it.id}`}
                            onClick={() => setLightbox({ items: profileItems, idx: i })}
                            aria-label={`Open ${it.isVideo ? "video" : "image"} ${i + 1} of ${profileItems.length}`}
                          >
                            {it.isVideo ? <video src={it.src} muted /> : <img src={it.src} alt={it.title} />}
                            <span className="media-tile-play" aria-hidden>{it.isVideo ? "" : "⤢"}</span>
                          </button>
                        ))}
                      </div>
                      {viewable.length > PROFILE_GALLERY_LIMIT && (
                        <Link
                          className="btn btn-ghost btn-sm artist-media-more"
                          to={`/artist/${profile.slug || artistId}/media`}
                          data-testid="artist-media-view-all"
                        >
                          View all {viewable.length} photos and videos
                        </Link>
                      )}
                    </>
                  );
                })()}
            </section>

            {artistEventGallery.length > 0 && (
              <section id="artist-section-event-gallery" className="card card-pad artist-profile-section" data-testid="artist-event-gallery">
                <div className="artist-event-gallery-heading">
                  <div>
                    <h2 className="artist-profile-section-title" style={{ marginBottom: 4 }}>Artist’s Event Gallery</h2>
                    <p className="text-muted fs-12" style={{ margin: 0 }}>BookTalent events featuring {profile.stage_name}.</p>
                  </div>
                  <span className="artist-event-gallery-count">{artistEventGallery.length} {artistEventGallery.length === 1 ? "event" : "events"}</span>
                </div>
                <div className="artist-event-gallery-grid">
                  {artistEventGallery.map((event) => {
                    const cover = (event.media || []).find((item) => item.mime?.startsWith("image/"));
                    const mediaCount = event.media?.length || 0;
                    return (
                      <Link
                        key={event.id}
                        to={`/events-gallery/${encodeURIComponent(event.id)}`}
                        className="artist-event-gallery-card"
                        data-testid={`artist-event-${event.id}`}
                      >
                        <div className="artist-event-gallery-cover">
                          {cover && (
                            <img
                              src={mediaUrl(cover.id)}
                              alt=""
                              loading="lazy"
                              onError={(error) => { error.currentTarget.style.display = "none"; }}
                            />
                          )}
                          <span className="artist-event-gallery-cover-label">BookTalent event</span>
                        </div>
                        <div className="artist-event-gallery-card-body">
                          <strong>{event.event_type || "BookTalent Event"}</strong>
                          <span>{[event.city, event.event_date].filter(Boolean).join(" · ") || "Event highlights"}</span>
                          <small>{mediaCount} published {mediaCount === 1 ? "media" : "media items"}</small>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <div className="artist-event-gallery-footer">
                  <Link className="btn btn-ghost btn-sm" to="/events-gallery">View all events <span aria-hidden="true">→</span></Link>
                </div>
              </section>
            )}

            <section id="artist-section-availability" className="card card-pad artist-profile-section" data-testid="tab-availability-content">
                <h2 className="artist-profile-section-title">Availability</h2>
                <div style={{ marginBottom: "var(--space-3)", color: "var(--white-muted)", fontSize: 13 }}>
                   Pick a date to check {profile.stage_name.split(" ")[0]}'s live availability. Dates in red are already booked or blocked.
                </div>
                <div className="artist-profile-calendar">
                  <AvailabilityCalendar artistUserId={profile.user_id} onPick={handleAvailabilityPick} />
                </div>
            </section>

            <section id="artist-section-reviews" className="card card-pad artist-profile-section" data-testid="tab-reviews-content">
              <div className="flex-between mb-16">
                <h2 className="artist-profile-section-title" style={{ marginBottom: 0 }}>Reviews</h2>
                <div className="flex items-center gap-8">
                  {myReview && (
                    <button
                      type="button"
                      className="artist-my-review-button"
                      onClick={() => nav(`/customer?tab=reviews&review=${encodeURIComponent(myReview.id)}`)}
                      data-testid="my-review-button"
                    >My Review</button>
                  )}
                  <div className="text-muted fs-12" data-testid="artist-review-summary">
                    {Number(profile.review_count) || 0} reviews
                    {Number(profile.review_count) > 0 && ` · ${Number(profile.rating_avg).toFixed(1)} / 5`}
                  </div>
                </div>
              </div>

              {reviewsError && <div className="text-red fs-12 mb-12" role="alert">{reviewsError}</div>}
              {!reviewsError && reviews.length === 0 && (
                <div className="text-muted fs-13 mb-16" data-testid="artist-no-reviews">No published reviews yet. Be the first to share your experience.</div>
              )}
              <div
                className="artist-reviews-scroller"
                ref={reviewsScrollerRef}
                role="region"
                aria-label="Artist reviews"
                data-testid="artist-reviews-scroller"
              >
                {[...reviews, ...(myReview && !reviews.some((review) => review.id === myReview.id) ? [myReview] : [])].map((review) => (
                  <article
                    key={review.id}
                    id={myReview?.id === review.id ? `my-review-${review.id}` : undefined}
                    className={`card card-pad artist-review-slide${myReview?.id === review.id ? " artist-review-own" : ""}`}
                    data-testid={myReview?.id === review.id ? `my-review-${review.id}` : `public-review-${review.id}`}
                  >
                    <div className="flex-between mb-8">
                      <div>
                        <div className="fw-600">{review.customer_name || "BookTalent Member"}</div>
                        {myReview?.id === review.id && <div className="artist-review-own-label">Your review</div>}
                      </div>
                      <div className="text-right">
                        <time className="text-muted fs-11">{review.created_at?.slice(0, 10)}</time>
                        {myReview?.id === review.id && review.moderated !== "approved" && (
                          <div className="artist-review-status">Awaiting approval</div>
                        )}
                      </div>
                    </div>
                    <div className="text-gold fs-14 mb-8" aria-label={`${review.rating} out of 5 stars`}>
                      {"★".repeat(Number(review.rating) || 0)}{"☆".repeat(5 - (Number(review.rating) || 0))}
                    </div>
                    {(review.event_type || review.event_note) && (() => {
                      const eventLabel = review.event_note
                        || [review.event_type, review.event_date, review.event_city].filter(Boolean).join(" · ");
                      return (
                        <div className="text-muted fs-11 mb-8" data-testid={`review-event-${review.id}`}>
                          Event:{" "}
                          {review.event_id
                            ? <Link to={`/events-gallery/${review.event_id}`}>{eventLabel}</Link>
                            : eventLabel}
                        </div>
                      );
                    })()}
                    <p className="fs-13" style={{ margin: 0, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{review.text}</p>
                    {((review.photos || []).length > 0 || (review.videos || []).length > 0) && (
                      <div className="review-media-grid mt-12" data-testid={`review-media-${review.id}`}>
                        {(review.photos || []).map((mediaId) => (
                          <a key={mediaId} className="review-media-tile" href={mediaUrl(mediaId)} target="_blank" rel="noopener noreferrer">
                            <img src={mediaUrl(mediaId)} alt="Photo from this review" loading="lazy" />
                          </a>
                        ))}
                        {(review.videos || []).map((mediaId) => (
                          <div key={mediaId} className="review-media-tile is-video">
                            <video src={mediaUrl(mediaId)} controls playsInline preload="metadata" />
                          </div>
                        ))}
                      </div>
                    )}
                    {myReview?.id === review.id && (
                      <button type="button" className="artist-review-edit mt-12" onClick={startEditingReview} data-testid={`edit-profile-review-${review.id}`}>
                        Edit Review
                      </button>
                    )}
                    {review.reply && (
                      <div className="mt-12" style={{ padding: "var(--space-3)", background: "var(--bg3)", borderRadius: 10 }}>
                        <div className="text-muted fs-11 mb-4">{profile.stage_name}'s reply</div>
                        <div className="fs-13">{review.reply}</div>
                      </div>
                    )}
                  </article>
                ))}
              </div>
              {reviews.length > 1 && (
                <div className="artist-reviews-navigation" aria-label="Review navigation">
                  <span className="artist-reviews-navigation-label">Browse reviews</span>
                  <div className="artist-reviews-arrows">
                    <button
                      type="button"
                      className="artist-reviews-arrow"
                      onClick={() => scrollReviews(-1)}
                      aria-label="Scroll to previous reviews"
                      data-testid="reviews-previous"
                    ><ChevronLeft size={18} aria-hidden="true" /></button>
                    <button
                      type="button"
                      className="artist-reviews-arrow"
                      onClick={() => scrollReviews(1)}
                      aria-label="Scroll to more reviews"
                      data-testid="reviews-next"
                    ><ChevronRight size={18} aria-hidden="true" /></button>
                  </div>
                </div>
              )}

              <div className="card card-pad mt-16" data-testid="write-artist-review">
                <h3 className="fw-700 fs-15 mb-8">{editingReviewId ? "Edit your review" : `Rate ${profile.stage_name}`}</h3>
                <p className="text-muted fs-12 mb-12">Reviews are checked by BookTalent before they appear publicly.</p>
                {!user ? (
                  <Link
                    className="btn btn-gold btn-sm"
                    to={`/signup?role=customer&next=${encodeURIComponent(`/artist/${id}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`)}`}
                    data-testid="review-signup-link"
                  >Sign up to write a review</Link>
                ) : user.id === profile.user_id ? (
                  <div className="text-muted fs-12">You can’t review your own artist profile.</div>
                ) : !["customer", "corporate"].includes(user.role) ? (
                  <div className="text-muted fs-12">Sign in with a customer account to write a review.</div>
                ) : (
                  <form onSubmit={submitReview} data-testid="artist-review-form">
                    <fieldset style={{ border: 0, padding: 0, margin: "0 0 var(--space-3)" }}>
                      <legend className="field-label">Your rating</legend>
                      <div className="flex gap-8" role="group" aria-label="Choose a star rating">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setReviewRating(rating)}
                            aria-label={`${rating} star${rating === 1 ? "" : "s"}`}
                            aria-pressed={reviewRating === rating}
                            data-testid={`review-star-${rating}`}
                            style={{ color: rating <= reviewRating ? "var(--gold)" : "var(--white-muted)", fontSize: 24, padding: "2px 6px" }}
                          >★</button>
                        ))}
                      </div>
                    </fieldset>
                    <div className="field">
                      <label className="field-label" htmlFor="artist-review-text">Your review</label>
                      <textarea
                        id="artist-review-text"
                        className="field-input"
                        rows={4}
                        maxLength={3000}
                        value={reviewText}
                        onChange={(event) => setReviewText(event.target.value)}
                        placeholder="Share what you liked about the performance…"
                        data-testid="artist-review-text"
                      />
                    </div>
                    {editingReviewId && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={cancelEditingReview} data-testid="cancel-profile-review-edit">
                        Cancel edit
                      </button>
                    )}
                    {!editingReviewId && (
                      <>
                        <div className="field">
                          <label className="field-label" htmlFor="artist-review-event">Which event is this about? (optional)</label>
                          <select
                            id="artist-review-event"
                            className="field-input"
                            value={reviewEventChoice}
                            onChange={(event) => setReviewEventChoice(event.target.value)}
                            data-testid="artist-review-event"
                          >
                            <option value="">No specific event</option>
                            {reviewEvents.length > 0 && (
                              <optgroup label="Your bookings">
                                {reviewEvents.map((reviewEvent) => (
                                  <option key={reviewEvent.id} value={`booking:${reviewEvent.id}`}>
                                    {reviewEvent.event_type} · {reviewEvent.event_date || "Date unavailable"}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {(() => {
                              const ownIds = new Set(reviewEvents.map((reviewEvent) => reviewEvent.id));
                              const publicEvents = artistEventGallery.filter((galleryEvent) => !ownIds.has(galleryEvent.id));
                              return publicEvents.length > 0 && (
                                <optgroup label={`Events by ${profile.stage_name}`}>
                                  {publicEvents.map((galleryEvent) => (
                                    <option key={galleryEvent.id} value={`event:${galleryEvent.id}`}>
                                      {[galleryEvent.event_type, galleryEvent.event_date, galleryEvent.city].filter(Boolean).join(" · ")}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            })()}
                            <option value="other">Other — I'll describe the event</option>
                          </select>
                          {reviewEventsError && <div className="text-muted fs-11 mt-4" role="status">{reviewEventsError}</div>}
                        </div>
                        {reviewEventChoice === "other" && (
                          <div className="field">
                            <label className="field-label" htmlFor="artist-review-event-note">Describe the event</label>
                            <input
                              id="artist-review-event-note"
                              className="field-input"
                              maxLength={200}
                              value={reviewEventNote}
                              onChange={(event) => setReviewEventNote(event.target.value)}
                              placeholder="e.g. Sangeet night at a Jaipur palace, Dec 2025"
                              data-testid="artist-review-event-note"
                            />
                          </div>
                        )}
                        <div className="field">
                          <div className="field-label">Photos &amp; videos (optional)</div>
                          <div className="review-media-pickers">
                            <label className="btn btn-ghost btn-sm" data-testid="artist-review-add-photos">
                              Add photos
                              <input type="file" accept="image/*" multiple hidden onChange={(event) => { addReviewMedia(event.target.files, "photo"); event.target.value = ""; }} />
                            </label>
                            <label className="btn btn-ghost btn-sm" data-testid="artist-review-add-videos">
                              Add videos
                              <input type="file" accept="video/*" multiple hidden onChange={(event) => { addReviewMedia(event.target.files, "video"); event.target.value = ""; }} />
                            </label>
                            <span className="text-muted fs-11">Up to 5 photos and 2 videos, 14 MB in total. Shown after approval.</span>
                          </div>
                          {(reviewPhotos.length > 0 || reviewVideos.length > 0) && (
                            <div className="review-media-grid mt-8" data-testid="artist-review-media-preview">
                              {reviewPhotos.map((file, index) => (
                                <div key={`p-${index}`} className="review-media-tile">
                                  <img src={file.url} alt={file.name} />
                                  <button type="button" aria-label={`Remove ${file.name}`} onClick={() => setReviewPhotos((items) => items.filter((_, i) => i !== index))}>×</button>
                                </div>
                              ))}
                              {reviewVideos.map((file, index) => (
                                <div key={`v-${index}`} className="review-media-tile">
                                  <video src={file.url} muted playsInline preload="metadata" />
                                  <button type="button" aria-label={`Remove ${file.name}`} onClick={() => setReviewVideos((items) => items.filter((_, i) => i !== index))}>×</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    <button
                      type="submit"
                      className="btn btn-gold btn-sm"
                      disabled={reviewSubmitting || !reviewRating || !reviewText.trim()}
                      data-testid="submit-artist-review"
                    >{reviewSubmitting ? "Submitting…" : "Post review"}</button>
                  </form>
                )}
              </div>
            </section>

          </div>

          <div className="artist-booking-sidebar" data-testid="booking-sidebar">
            <div className={`card card-pad artist-booking-card ${selectedPkg ? "has-selected-package" : ""}`}>
              <div className="artist-booking-heading">
                <div>
                  <h2 className="artist-profile-section-title">Book {profile.stage_name}</h2>
                  {selectedPkg && (
                    <span className="artist-booking-selected-badge" role="status" data-testid="booking-selected-package-badge">
                      {selectedPkg.name} selected
                    </span>
                  )}
                </div>
                {!selectedPkg && cheapestPkg && (
                  <div className="artist-booking-starting-price">
                    <span className="text-muted fs-11">Starting from</span>
                    <strong>{fmtINRFull(cheapestPkg.price)}</strong>
                  </div>
                )}
              </div>
              <div className="field">
                <div className="field-label">Choose a package <span className="fs-11 text-muted" style={{ fontWeight: 400 }}>required</span></div>
                <select
                  className="field-input"
                  value={selectedPkg?.id || ""}
                  onChange={(e) => selectPackage(packages.find((p) => p.id === e.target.value) || null)}
                  data-testid="pkg-select"
                >
                  <option value="">Select a package</option>
                  {packages.map((p) => {
                    const qp = quote?.packages?.find((x) => x.id === p.id);
                    const price = qp?.quoted_price ?? p.price;
                    return <option key={p.id} value={p.id}>{p.name} — {fmtINRFull(price)}</option>;
                  })}
                </select>
              </div>
              {!selectedPkg && (
                <div className="text-muted fs-12" data-testid="package-selection-prompt">
                  Choose a package to see its price and continue booking.
                </div>
              )}
              {selectedPkg && (() => {
                // Resolve the price the customer should see:
                //   • no venue yet → package base price ("Starting price")
                //   • quote in flight OR no quote → still base
                //   • quote arrived → outstation-adjusted `quoted_price` from
                //     the matching package in the /quote response
                const quotedPkg = quote?.packages?.find((p) => p.id === selectedPkg.id);
                const displayPrice = quotedPkg?.quoted_price ?? selectedPkg.price;
                const surcharge = quote?.outstation_surcharge_pct || 0;
                const packageFeatures = Array.isArray(selectedPkg.features)
                  ? selectedPkg.features.filter(Boolean)
                  : [];
                const travelModes = Array.isArray(selectedPkg.travel_modes)
                  ? selectedPkg.travel_modes.filter(Boolean)
                  : [];
                return (
                <>
                  <div className="font-serif" style={{ fontSize: 32, fontWeight: 700, color: "var(--gold-light)" }}>
                    {fmtINRFull(displayPrice)}
                  </div>
                  <div className="text-muted fs-12 mb-16" data-testid="artist-price-label">
                    {isOutstation && surcharge > 0
                      ? <>Outstation quote · {selectedPkg.name} <span className="text-gold">(+{surcharge}%)</span></>
                      : <>Starting price · {selectedPkg.name}</>}
                  </div>

                  <div
                    className="package-booking-details"
                    data-testid="selected-package-details"
                    style={{
                      padding: "var(--space-3)",
                      marginBottom: "var(--space-4)",
                      borderRadius: 10,
                      background: "var(--gold-dim)",
                      border: "1px solid var(--gold)",
                      fontSize: 12,
                    }}
                  >
                    <div className="fw-700 mb-8" data-testid="selected-package-confirmation">
                      Selected for booking · {selectedPkg.name}
                    </div>
                    {selectedPkg.description && (
                      <div className="text-muted mb-8" data-testid="selected-package-description">
                        {selectedPkg.description}
                      </div>
                    )}
                    <div style={{ display: "grid", gap: 6 }}>
                      {selectedPkg.duration && (
                        <div data-testid="selected-package-duration">
                          <span className="text-muted">Performance time: </span>{selectedPkg.duration}
                        </div>
                      )}
                      {Number(selectedPkg.team_size) > 0 && (
                        <div data-testid="selected-package-team-size">
                          <span className="text-muted">Artist team: </span>
                          {selectedPkg.team_size} {Number(selectedPkg.team_size) === 1 ? "person" : "people"}
                        </div>
                      )}
                      {selectedPkg.arrival_buffer_days > 0 && (
                        <div data-testid="selected-package-arrival-buffer">
                          <span className="text-muted">Team arrives: </span>
                          {selectedPkg.arrival_buffer_days} {Number(selectedPkg.arrival_buffer_days) === 1 ? "day" : "days"} before the event
                        </div>
                      )}
                      {selectedPkg.travel_required && (
                        <div data-testid="selected-package-travel">
                          <span className="text-muted">Travel: </span>
                          {travelModes.length ? travelModes.join(", ") : "Required"}
                          {selectedPkg.travel_charge_model ? ` · ${selectedPkg.travel_charge_model}` : ""}
                        </div>
                      )}
                      {selectedPkg.accommodation_required && (
                        <div data-testid="selected-package-accommodation">
                          <span className="text-muted">Accommodation: </span>
                          {selectedPkg.hotel_category || "Required"}
                        </div>
                      )}
                      {selectedPkg.local_transport_required && (
                        <div data-testid="selected-package-local-transport">Local transport required</div>
                      )}
                      {selectedPkg.meals_required && (
                        <div data-testid="selected-package-meals">Meals and hospitality required</div>
                      )}
                    </div>
                    {packageFeatures.length > 0 && (
                      <div style={{ marginTop: "var(--space-2)" }} data-testid="selected-package-features">
                        <div className="text-muted fs-11 mb-4">INCLUDED IN THIS PACKAGE</div>
                        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                          {packageFeatures.map((feature, index) => (
                            <li key={`${selectedPkg.id}-detail-${index}`}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Venue-first prompt (Iter 52.6) — must be filled before Book Now */}
                  <div className="field venue-first-field">
                    <div className="field-label">
                       Where's your event?
                      <span className="fs-11 text-muted" style={{ fontWeight: 400, marginLeft: 6 }}>required</span>
                    </div>
                    <CitySearchField
                      className="field-input"
                      city={eventCity}
                      cities={cityOptions}
                      popularCities={popularCityOptions}
                      cityAliases={cityAliases}
                      allowCustom
                      onCityChange={(city) => {
                        setEventCity(city);
                        setOutstationAck(false);
                      }}
                      listId="artist-event-city-options"
                      placeholder="Search or enter any Indian city"
                      data-testid="venue-first-input"
                    />
                    {cityOptionsError && (
                      <div className="text-muted fs-11 mt-4">City suggestions are temporarily unavailable. You can still enter your city.</div>
                    )}
                    {cityTouched && quoting && (
                      <div className="text-muted fs-11 mt-4">Checking availability & pricing…</div>
                    )}
                    {cityTouched && quote && !quoting && (
                      <div className={`venue-status-chip ${isOutstation ? "outstation" : "local"}`} data-testid="venue-status-chip">
                        {isOutstation
                          ? <> <span>Outstation booking · Artist based in {quote.artist_city}</span></>
                          : <> <span>Local booking · Same city as artist</span></>}
                      </div>
                    )}
                  </div>

                  {/* Outstation Terms — only rendered when the venue quote flags outstation */}
                  {isOutstation && quote && (
                    <div className="venue-outstation-card" data-testid="venue-outstation-card">
                      <div className="fw-700 text-gold fs-12 mb-6" style={{ letterSpacing: ".08em", textTransform: "uppercase" }}>
                         Travel Terms
                      </div>
                      <div className="fs-12 mb-8" style={{ lineHeight: 1.5 }}>
                        {travelCostDisclosure(quotedPackage, quote.rider)}
                      </div>
                      {requiresTravelTermsAck && (
                        <label className="flex items-start gap-8" style={{ cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={outstationAck}
                            onChange={(e) => setOutstationAck(e.target.checked)}
                            data-testid="venue-outstation-ack"
                            style={{ marginTop: "var(--space-1)", flex: "none" }}
                          />
                          <span className="fs-12">
                            I have reviewed these travel terms and agree to settle the listed additional costs directly with the artist.
                          </span>
                        </label>
                      )}
                      {quote.booking_allowed === false && (
                        <div className="text-red fs-12 mt-8" data-testid="booking-location-blocked">
                          {quote.booking_block_reason || "This artist is not available for the selected event location."}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Detailed rider — sourced from the artist's onboarding questionnaire.
                      Rendered only for outstation quotes so local bookings aren't cluttered. */}
                  {isOutstation && quote?.rider && (
                    <TravelRiderCard
                      rider={quote.rider}
                      artistCity={quote.artist_city}
                      eventCity={quote.event_city}
                      compact
                    />
                  )}

                  <button
                    className="btn btn-gold btn-block"
                    onClick={() => startBooking()}
                    data-testid="confirm-booking-btn"
                    title={bookingBlocked && cityTouched ? quote?.booking_block_reason || "Review the location and travel terms above" : undefined}
                  >
                     Book Now
                  </button>

                  {/* Iter 71 — Secondary actions with LoginGate for guests. */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={toggleFavorite}
                      data-testid="favorite-artist-btn"
                      aria-pressed={isFavorite}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--space-1)",
                        color: isFavorite ? "#ff6b81" : undefined,
                        borderColor: isFavorite ? "rgba(255,107,129,0.5)" : undefined,
                      }}
                    >
                      <Heart size={14} fill={isFavorite ? "#ff6b81" : "none"} />
                      {isFavorite ? "Saved" : "Favorite"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={messageArtist}
                      data-testid="message-artist-btn"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--space-1)" }}
                    >
                      <MessageCircle size={14} /> Message
                    </button>
                  </div>
                  {!cityTouched && (
                    <div className="text-muted fs-11 mt-8" style={{ textAlign: "center" }}>
                      Enter your event city above to unlock booking
                    </div>
                  )}
                </>
                );
              })()}
              {selectedPkg && techRiderFile?.mime === "application/pdf" && (
                <div className="artist-tech-rider-panel" data-testid="profile-tech-rider">
                  <div>
                    <div className="fw-700 fs-12">Production information</div>
                    <div className="text-muted fs-11 mt-4">
                      Review the artist’s technical requirements before continuing.
                    </div>
                  </div>
                  <TechRiderLink
                    artistId={artistId}
                    filename={techRiderFile.filename || "Tech Rider PDF"}
                    className="btn btn-ghost btn-block"
                    testId="profile-tech-rider-preview"
                    onUnauthenticated={() => {
                      if (user) return true;
                      secondaryGate.promptLogin({
                        title: "Sign in to view the Tech Rider",
                        message: "Create a free BookTalent account to view this artist's technical setup PDF.",
                      });
                      return false;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
      {selectedPkg && (
        <div className="mobile-book-bar" data-testid="mobile-book-bar">
          <div className="mobile-book-bar-price">
            <div className="mobile-book-bar-label">Starting from</div>
            <div className="mobile-book-bar-amount">{fmtINRFull(selectedPkg.price)}</div>
          </div>
          <button
            type="button"
            className="btn btn-gold mobile-book-bar-cta"
            onClick={startBooking}
            data-testid="mobile-book-bar-btn"
          >
             Book Now
          </button>
        </div>
      )}
      {lightbox && (
        <MediaCarousel lightbox={lightbox} onClose={() => setLightbox(null)} onChange={setLightbox} />
      )}
      <LoginGate
        open={showLoginGate}
        onClose={() => setShowLoginGate(false)}
        title="Login to book this artist"
        message="You'll need a free BookTalent account to check availability and confirm your booking. We'll bring you right back here after."
        returnTo={loginGateReturnTo}
      />
      {/* Iter 71 — Secondary gate for Favorite / Message / Availability picks. */}
      {secondaryGate.gate}
      {/* Iter 73 — Location popup: opens when a guest / customer clicks
          Book Now without a city typed. Filling it here auto-updates the
          sidebar city input AND resumes the Book Now flow immediately. */}
      {locationPrompt.open && (
        <div
          data-testid="location-prompt-backdrop"
          onClick={() => setLocationPrompt({ open: false, value: locationPrompt.value, date: locationPrompt.date })}
          style={{
            position: "fixed", inset: 0, zIndex: 500,
            background: "rgba(36,34,31,0.36)", backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "var(--space-4)",
          }}
        >
          <form
            data-testid="location-prompt"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitLocationPrompt}
            style={{
              width: 440, maxWidth: "100%",
              background: "var(--bg1)",
              border: "1px solid var(--gold-border)",
              borderRadius: 20, padding: "var(--space-6) 24px var(--space-6)",
              color: "var(--white)",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            <div style={{ fontSize: 26, marginBottom: "var(--space-2)" }}></div>
            <h3 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 24, fontWeight: 700, margin: "0 0 var(--space-1)",
            }}>
              Where's your event?
            </h3>
            <p style={{ fontSize: 13, lineHeight: 1.55, color: "var(--white-muted)", margin: "0 0 var(--space-4)" }}>
              {locationPrompt.date
                ? `Choose your event city to continue with your selected date, ${new Date(`${locationPrompt.date}T00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}.`
                : "Tell us your event city so we can quote the right price — Mumbai, Delhi, Bengaluru, etc. We'll auto-fill this for you and continue."}
            </p>
            <CitySearchField
              autoFocus
              className="field-input"
              city={locationPrompt.value}
              cities={cityOptions}
              popularCities={popularCityOptions}
              cityAliases={cityAliases}
              allowCustom
              onCityChange={(value) => setLocationPrompt((prompt) => ({ ...prompt, value }))}
              listId="artist-location-prompt-city-options"
              placeholder="Search or enter any Indian city"
              data-testid="location-prompt-input"
              style={{ width: "100%", marginBottom: "var(--space-3)" }}
            />
            {cityOptionsError && (
              <div className="text-muted fs-11 mb-12">City suggestions are temporarily unavailable. You can still enter your city.</div>
            )}
            <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-ghost"
                data-testid="location-prompt-cancel"
                onClick={() => setLocationPrompt({ open: false, value: "", date: "" })}
              >Cancel</button>
              <button
                type="submit"
                className="btn btn-gold"
                data-testid="location-prompt-continue"
                disabled={(locationPrompt.value || "").trim().length < 2}
              >{locationPrompt.date ? "Continue with date →" : "Continue to Book →"}</button>
            </div>
          </form>
        </div>
      )}
</div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Iter 55 — QuestionnairePanel
// Renders every non-empty answer from the artist's onboarding questionnaire
// in a customer-friendly way, grouped by the "section" hint the question
// declared (Travel, Hospitality, Sound, etc.). Photo / video question
// answers are NOT rendered inline — instead we surface a "See gallery" chip
// that switches to the Media tab, so uploads always live in one place.
// ─────────────────────────────────────────────────────────────────────────
const MEDIA_TYPES = new Set(["photo", "photos", "video", "videos", "media", "attachment", "file", "image"]);

function formatAnswer(a) {
  const v = a.answer;
  if (a.id === "minimum_package_price") return fmtINRFull(Number(v));
  if (a.type === "toggle" || typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length === 0 ? "—" : v.join(" · ");
  if (typeof v === "object" && v !== null) {
    return Object.entries(v)
      .filter(([, val]) => val !== null && val !== "" && val !== false)
      .map(([k, val]) => `${k}: ${Array.isArray(val) ? val.join(", ") : val}`)
      .join(" · ");
  }
  return String(v);
}

function QuestionnairePanel({ about, onMediaClick }) {
  if (!about) return null;
  const all = [...(about.universal || []), ...(about.category || [])];
  if (all.length === 0) return null;

  // The "Tell us about yourself" section repeats details already shown in
  // the profile header, facts, trust belt, and gallery.
  const profileSummaryAnswers = new Set([
    "stage_name", "legal_name", "gender", "category", "years_experience",
    "languages", "base_city", "city", "about", "event_types",
    "events_performed", "profile_photo", "cover_photo", "gallery_images",
    "intro_video", "performance_videos",
  ]);
  const displayedAnswers = all.filter((answer) => (
    answer.section !== "Tell us about yourself" && !profileSummaryAnswers.has(answer.id)
  ));
  if (displayedAnswers.length === 0) return null;
  // Split media-type answers so we can point customers to the Media tab.
  const mediaAnswers = displayedAnswers.filter((a) => MEDIA_TYPES.has((a.type || "").toLowerCase()));
  const nonMedia = displayedAnswers.filter((a) => !MEDIA_TYPES.has((a.type || "").toLowerCase()));
  // Iter 56 — Backend attaches the newest matching media asset per media-type
  // answer so we can render thumbnail chips instead of a generic hint.
  const mediaMatches = about.media_matches || [];
  const matchByQid = Object.fromEntries(mediaMatches.map((m) => [m.question_id, m]));

  // Group by section (fall back to "Details").
  const bySection = {};
  for (const a of nonMedia) {
    const s = a.section || "Details";
    (bySection[s] = bySection[s] || []).push(a);
  }
  // Preserve a sensible ordering: Travel, Hospitality, Sound, Technical,
  // Availability, then everything else alphabetically.
  const ORDER = ["Travel", "Hospitality", "Sound", "Technical", "Availability", "Preferences", "Performance", "Details"];
  const sections = Object.keys(bySection).sort((a, b) => {
    const ai = ORDER.indexOf(a), bi = ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  return (
    <div className="qa-panel" data-testid="qa-panel" style={{ marginTop: "var(--space-6)" }}>
      <div className="divider" style={{ margin: "var(--space-1) 0 var(--space-4)" }} />
      <h3 className="card-title mb-16" style={{ fontSize: 18 }}>Additional Details</h3>
      <div style={{ fontSize: 12, color: "var(--white-muted)", marginBottom: "var(--space-3)" }}>
        Profile details from {displayedAnswers.length} questionnaire answers.
      </div>

      {sections.map((s) => (
        <div key={s} className="qa-section" data-testid={`qa-section-${s.toLowerCase()}`} style={{ marginBottom: "var(--space-4)" }}>
          <div style={{ fontSize: 11, letterSpacing: ".16em", color: "var(--gold-light)", textTransform: "uppercase", marginBottom: "var(--space-2)" }}>{s}</div>
          <div className="grid grid-2" style={{ gap: "var(--space-2)" }}>
            {bySection[s].map((a) => (
              <div key={a.id} style={{ background: "var(--glass)", borderRadius: 10, padding: "var(--space-3) 14px", border: "1px solid var(--glass-border)" }} data-testid={`qa-item-${a.id}`}>
                <div className="text-muted fs-11 mb-4">{a.question.toUpperCase()}</div>
                <div className="fs-13" style={{ lineHeight: 1.5 }}>{formatAnswer(a)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {mediaAnswers.length > 0 && (
        <div className="qa-media-section" data-testid="qa-media-section" style={{ marginTop: "var(--space-3)" }}>
          <div style={{ fontSize: 11, letterSpacing: ".16em", color: "var(--gold-light)", textTransform: "uppercase", marginBottom: "var(--space-2)" }}>Media Attachments</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "var(--space-2)" }}>
            {mediaAnswers.map((a) => {
              const m = matchByQid[a.id];
              const isMissing = !m;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={onMediaClick}
                  data-testid={isMissing ? `qa-media-missing-${a.id}` : `qa-media-chip-${a.id}`}
                  className={`qa-media-chip ${isMissing ? "qa-media-chip-missing" : ""}`}
                  title={isMissing ? "Not uploaded — ask the artist to add this to their gallery" : `View ${a.question} in the artist's gallery`}
                  style={{
                    display: "flex", gap: "var(--space-2)", alignItems: "center",
                    background: isMissing ? "rgba(255,255,255,0.02)" : "var(--glass)",
                    border: `1px ${isMissing ? "dashed" : "solid"} ${isMissing ? "rgba(255,255,255,0.15)" : "var(--glass-border)"}`,
                    borderRadius: 10, padding: "var(--space-2)", cursor: "pointer",
                    color: "inherit", textAlign: "left", font: "inherit",
                    transition: "transform 0.15s, border-color 0.2s",
                    opacity: isMissing ? 0.85 : 1,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = isMissing ? "rgba(246,211,102,0.4)" : "rgba(246,211,102,0.5)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = isMissing ? "rgba(255,255,255,0.15)" : "var(--glass-border)"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  {m ? (
                    m.mime && m.mime.startsWith("video") ? (
                      <div style={{ width: 56, height: 56, borderRadius: 8, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", flexShrink: 0, fontSize: 22 }}></div>
                    ) : (
                      <img
                        src={thumbUrl(m.media_id)}
                        alt=""
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        style={{ width: 56, height: 56, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
                      />
                    )
                  ) : (
                    <div style={{
                      width: 56, height: 56, borderRadius: 8,
                      background: "repeating-linear-gradient(45deg, rgba(255,255,255,0.03), rgba(255,255,255,0.03) 4px, rgba(255,255,255,0.06) 4px, rgba(255,255,255,0.06) 8px)",
                      display: "grid", placeItems: "center", flexShrink: 0,
                      fontSize: 22, color: "rgba(255,255,255,0.4)",
                    }}></div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="fs-12 fw-600" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.question}</div>
                    <div style={{ marginTop: "var(--space-1)", fontSize: 11, color: isMissing ? "rgba(246,211,102,0.85)" : "var(--white-muted)" }}>
                      {isMissing ? "Ask artist for this →" : "View in gallery →"}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
